import React, { Component } from "react";
import { connect } from "react-redux";
import {
  nextQuestion,
  recordActivityCode,
  setActivityTopCodes,
  setActivityScores,
} from "../../actions/assessment";
import QuestionNumber from "../QuestionNumber";
import styled, { keyframes } from "styled-components";
import { FiAward } from "react-icons/fi";
import axios from "axios";
import Webcam from "react-webcam";
import config from "../../config/config-secret.json";
import { Translate, I18n } from "react-redux-i18n";

let count = 0;

// ─── Constants ────────────────────────────────────────────────────────────────

const TIE_BREAKER_ACTIVITY_KEYS = {
  R: "tie_break_activity_r",
  I: "tie_break_activity_i",
  A: "tie_break_activity_a",
  S: "tie_break_activity_s",
  E: "tie_break_activity_e",
  C: "tie_break_activity_c",
};

// Canonical order, used to break ties that the tester is never asked about.
const CODE_ORDER = ["R", "I", "A", "S", "E", "C"];

const PERCENT_PER_RAW_POINT = 20;
const TIE_STEP_PENALTY = 5;
const TOP_CODE_COUNT = 3;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function computeRawScores(codes) {
  const scores = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
  for (const code of codes) {
    if (code in scores) scores[code]++;
  }
  return scores;
}

// Buckets the six codes by identical raw score, highest score first. Codes
// inside a bucket keep their canonical order until a tie break reorders them.
function buildScoreGroups(rawScores) {
  const entries = CODE_ORDER.map((code) => [code, rawScores[code] || 0]).sort(
    (a, b) => b[1] - a[1],
  );

  const groups = [];
  for (const [code, score] of entries) {
    const last = groups[groups.length - 1];
    if (!last || last.score !== score) {
      groups.push({ score, codes: [code] });
    } else {
      last.codes.push(code);
    }
  }
  return groups;
}

// Every bucket holding more than one code gets its own tie break, wherever it
// sits in the ranking — a tie below the top 3 still has to be ordered so each
// code ends up with a distinct percentage. A bucket of n codes takes n - 1
// picks to order fully; the last code is settled by elimination.
//
// The one bucket we skip is a group of zeros that cannot reach the top 3:
// every ordering scores all of its codes 0, so there is nothing to ask about.
function getTieBreakPlan(rawScores) {
  const groups = buildScoreGroups(rawScores);

  const tieBreaks = [];
  let position = 0;

  for (const group of groups) {
    const startPosition = position;
    position += group.codes.length;

    if (group.codes.length < 2) continue;
    if (group.score === 0 && startPosition >= TOP_CODE_COUNT) continue;

    tieBreaks.push({
      score: group.score,
      codes: group.codes,
      steps: group.codes.length - 1,
    });
  }

  return { groups, tieBreaks, needsTieBreak: tieBreaks.length > 0 };
}

// Flattens the buckets into one ranked list of all six codes. picksByTieBreak[i]
// holds the codes the tester ranked, best first, for plan.tieBreaks[i].
function resolveCodeOrder(plan, picksByTieBreak) {
  const rankedByScore = new Map();

  plan.tieBreaks.forEach((tieBreak, index) => {
    const picks = picksByTieBreak[index] || [];
    const unpicked = tieBreak.codes.filter((code) => !picks.includes(code));
    rankedByScore.set(tieBreak.score, [...picks, ...unpicked]);
  });

  const order = [];
  for (const group of plan.groups) {
    order.push(...(rankedByScore.get(group.score) || group.codes));
  }
  return order;
}

// Base percentage is the raw score x 20. Within a tie, each code after the
// winner steps down 5 points: raw 2,2,2 becomes 40, 35, 30.
function computeFinalPercentScores(plan, codeOrder) {
  const percentScores = {};
  let index = 0;

  for (const group of plan.groups) {
    for (let i = 0; i < group.codes.length; i++) {
      percentScores[codeOrder[index + i]] = Math.max(
        0,
        group.score * PERCENT_PER_RAW_POINT - i * TIE_STEP_PENALTY,
      );
    }
    index += group.codes.length;
  }

  return percentScores;
}

// ─── Tie-Break Overlay (styled-components) ───────────────────────────────────

const overlayIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const OverlayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const OverlayCard = styled.div`
  background: linear-gradient(145deg, #0f2236 0%, #112d4a 100%);
  border: 1px solid rgba(38, 125, 216, 0.25);
  border-radius: 20px;
  padding: 36px 32px;
  width: 100%;
  max-width: 650px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  animation: ${overlayIn} 0.3s ease both;
`;

const TieBreakTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 0.7px;
  color: rgba(255, 255, 255, 0.96);
  margin: 0 0 30px;
  line-height: 1.4;
`;

const TieBreakSubtitle = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.78);
  margin: 8px 0 0;
  letter-spacing: 0.3px;
  line-height: 1.6;
`;

const TieBreakProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
`;

const ProgressText = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  background: rgba(255, 255, 255, 0.18);
  padding: 4px 12px;
  border-radius: 20px;
`;

const ProgressDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#fff" : "rgba(255,255,255,0.2)")};
  box-shadow: ${({ active }) =>
    active ? "0 0 0 3px rgba(255,255,255,0.2)" : "none"};
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export class ActivityOptions extends Component {
  constructor(props) {
    super(props);
    this.webcamRef = React.createRef();
    this.state = {
      selectedId: null,
      pendingId: null,
      phase: "question",
      tieBreakPlan: null,
      rawScores: null,
      allCodes: null,
      // Index of the tie break being asked, and the picks made so far in it.
      tieBreakIndex: 0,
      tieBreakPicks: [],
      picksByTieBreak: [],
      tieBreakSelected: null,
    };
  }

  componentDidMount() {
    count = 0;
    console.log(
      "[ActivityOptions] choices structure (first item):",
      this.props.choices?.[0],
    );
    console.log("[ActivityOptions] all choices:", this.props.choices);
  }

  onUserMediaError = () => {
    alert("Can't access webcam, please contact administrator.");
  };

  onUserMedia = () => {};

  addCaptureImage = (imageb64, taken_at) => {
    axios
      .post(config.API_URL + `/administer_test/save_security_image/`, {
        imageb64,
        taken_at,
        tester_id: this.props.assessment.tester_id,
      })
      .catch((error) => {
        console.log(error);
      });
  };

  isLastQuestion = () => {
    const { exam_index, question_index, exams } = this.props.assessment;
    return question_index === exams[exam_index].questions.length - 1;
  };

  saveFinalScoringData = (allCodes, rawScores, top3, finalScores) => {
    const { tester_id, ...baseSessionInfo } = this.props.assessment;
    const enrichedSessionInfo = {
      ...baseSessionInfo,
      activity_codes: allCodes,
      activity_raw_scores: rawScores,
      activity_top_codes: top3,
      activity_scores: finalScores,
    };

    console.log("=== [ActivityOptions] saveFinalScoringData ===");
    console.log(
      "Endpoint:",
      config.API_URL + "/administer_test/update_tester_session_info/",
    );
    console.log("Field: last_session_info (JSON-stringified) — scoring keys:");
    console.log("  activity_codes      →", allCodes);
    console.log("  activity_raw_scores →", rawScores);
    console.log("  activity_top_codes  →", top3);
    console.log("  activity_scores     →", finalScores);
    console.log("Full payload:", {
      tester_id,
      last_session_info: enrichedSessionInfo,
    });

    return axios.post(
      config.API_URL + "/administer_test/update_tester_session_info/",
      {
        tester_id,
        last_session_info: JSON.stringify(enrichedSessionInfo),
      },
    );
  };

  handleSelect = (id) => {
    if (count >= 1 || this.state.selectedId !== null) return;
    this.setState({ selectedId: id });

    if (this.props.assessment.has_security_features) {
      this.addCaptureImage(this.webcamRef.current.getScreenshot(), new Date());
    }

    setTimeout(() => {
      count += 1;
      window.localStorage.setItem(
        this.props.assessment.key,
        JSON.stringify(this.props.assessment),
      );

      const selectedChoice = this.props.choices.find((c) => c.id === id);
      const valueText = selectedChoice?.value_text;

      const { tester_id, ...last_session_info } = this.props.assessment;

      console.log("=== [ActivityOptions] handleSelect — saving session ===");
      console.log(
        "Question ID:",
        this.props.question_id,
        "| Picked value_text:",
        valueText,
      );
      console.log(
        "Endpoint:",
        config.API_URL + "/administer_test/update_tester_session_info/",
      );
      console.log(
        "Field: last_session_info | activity_codes so far (preview):",
        [...this.props.assessment.activity_codes, valueText],
      );

      axios
        .post(config.API_URL + "/administer_test/update_tester_session_info/", {
          tester_id,
          last_session_info: JSON.stringify(last_session_info),
        })
        .then(() => {
          document.querySelector("#initloader").style.display = "none";
          const prevCodes = this.props.assessment.activity_codes;
          this.props.recordActivityCode(valueText);

          if (this.isLastQuestion()) {
            const allCodes = [...prevCodes, valueText];
            const rawScores = computeRawScores(allCodes);
            const tieBreakPlan = getTieBreakPlan(rawScores);

            console.log("=== [ActivityOptions] Last question reached ===");
            console.log("All codes collected:", allCodes);
            console.log("Raw scores:", rawScores);
            console.log("Tie-break plan:", tieBreakPlan);

            if (tieBreakPlan.needsTieBreak) {
              this.setState({
                phase: "tiebreak",
                tieBreakPlan,
                rawScores,
                allCodes,
                tieBreakIndex: 0,
                tieBreakPicks: [],
                picksByTieBreak: [],
                tieBreakSelected: null,
                pendingId: id,
              });
            } else {
              const codeOrder = resolveCodeOrder(tieBreakPlan, []);
              const finalScores = computeFinalPercentScores(
                tieBreakPlan,
                codeOrder,
              );
              const top3 = codeOrder.slice(0, TOP_CODE_COUNT);
              this.saveFinalScoringData(allCodes, rawScores, top3, finalScores)
                .catch((err) =>
                  console.error(
                    "[ActivityOptions] Failed to save scoring data:",
                    err,
                  ),
                )
                .finally(() => {
                  this.props.setActivityTopCodes(top3);
                  this.props.setActivityScores(finalScores);
                  this.props.nextQuestion(this.props.question_id, [id]);
                });
            }
          } else {
            this.props.nextQuestion(this.props.question_id, [id]);
          }
        })
        .catch((error) => {
          document.querySelector("#initloader").style.display = "none";
          console.error(error);
          const serverMessage =
            error.response?.data?.message ||
            error.response?.data ||
            error.message ||
            "Unknown error";
          alert(
            `There appears to be a problem sending data to our server.\nPlease check your internet connection and try refreshing your browser.\n\nError details: ${serverMessage}\n\nIf the issue persists, please contact your administrator.`,
          );
        });
    }, 380);
  };

  handleTieBreakPick = (code) => {
    if (this.state.tieBreakSelected !== null) return;
    const {
      tieBreakPlan,
      tieBreakIndex,
      tieBreakPicks,
      picksByTieBreak,
      rawScores,
      allCodes,
      pendingId,
    } = this.state;
    const currentTieBreak = tieBreakPlan.tieBreaks[tieBreakIndex];
    this.setState({ tieBreakSelected: code });

    console.log(
      `=== [ActivityOptions] Tie break ${tieBreakIndex + 1} of ${tieBreakPlan.tieBreaks.length}, step ${tieBreakPicks.length + 1} of ${currentTieBreak.steps} ===`,
    );
    console.log(
      "Picked code:",
      code,
      "→",
      I18n.t(TIE_BREAKER_ACTIVITY_KEYS[code]),
    );
    console.log("Picks so far in this tie break:", [...tieBreakPicks, code]);

    setTimeout(() => {
      const newPicks = [...tieBreakPicks, code];

      // Still ordering the current tie — ask the next step.
      if (newPicks.length < currentTieBreak.steps) {
        this.setState({ tieBreakPicks: newPicks, tieBreakSelected: null });
        return;
      }

      const newPicksByTieBreak = [...picksByTieBreak];
      newPicksByTieBreak[tieBreakIndex] = newPicks;

      // This tie is fully ordered — move on to the next tied group, if any.
      if (tieBreakIndex + 1 < tieBreakPlan.tieBreaks.length) {
        this.setState({
          picksByTieBreak: newPicksByTieBreak,
          tieBreakIndex: tieBreakIndex + 1,
          tieBreakPicks: [],
          tieBreakSelected: null,
        });
        return;
      }

      const codeOrder = resolveCodeOrder(tieBreakPlan, newPicksByTieBreak);
      const finalScores = computeFinalPercentScores(tieBreakPlan, codeOrder);
      const top3 = codeOrder.slice(0, TOP_CODE_COUNT);

      console.log("=== [ActivityOptions] Tie-break complete ===");
      console.log("Full ranked order:", codeOrder);
      console.log("Final top 3:", top3);
      console.log("Final percent scores:", finalScores);

      this.saveFinalScoringData(allCodes, rawScores, top3, finalScores)
        .catch((err) =>
          console.error("[ActivityOptions] Failed to save scoring data:", err),
        )
        .finally(() => {
          this.props.setActivityTopCodes(top3);
          this.props.setActivityScores(finalScores);
          this.props.nextQuestion(this.props.question_id, [pendingId]);
        });
    }, 380);
  };

  renderTieBreak() {
    console.log("[ActivityOptions] assessment:", this.props.assessment);
    const { tieBreakPlan, tieBreakIndex, tieBreakPicks, tieBreakSelected } =
      this.state;
    if (!tieBreakPlan) return null;

    const currentTieBreak = tieBreakPlan.tieBreaks[tieBreakIndex];
    const currentRound = tieBreakPicks.length + 1;
    const totalRounds = currentTieBreak.steps;
    const totalTieBreaks = tieBreakPlan.tieBreaks.length;
    const remaining = currentTieBreak.codes.filter(
      (c) => !tieBreakPicks.includes(c),
    );

    return (
      <OverlayBackdrop>
        <OverlayCard>
          <div style={{ marginBottom: 24 }}>
            <TieBreakProgress>
              <FiAward
                size={14}
                style={{ color: "rgba(255,255,255,0.75)", flexShrink: 0 }}
              />
              <ProgressText>
                {totalTieBreaks > 1 && (
                  <>
                    <Translate
                      value="tie_break_round"
                      curr={tieBreakIndex + 1}
                      total={totalTieBreaks}
                    />
                    {" · "}
                  </>
                )}
                <Translate
                  value="tie_break_step"
                  curr={currentRound}
                  total={totalRounds}
                />
              </ProgressText>
              {Array.from({ length: totalRounds }).map((_, i) => (
                <ProgressDot key={i} active={i < currentRound} />
              ))}
            </TieBreakProgress>
            <TieBreakSubtitle>
              <Translate value="tie_break_subtitle" />
            </TieBreakSubtitle>
          </div>

          <TieBreakTitle>
            <Translate value="tie_break_title" />
          </TieBreakTitle>

          <div
            className="choices-btn"
            data-toggle="buttons"
            key={`${tieBreakIndex}-${tieBreakPicks.length}`}
          >
            {remaining.map((code, index) => {
              const isSelected = tieBreakSelected === code;
              const isDimmed = tieBreakSelected !== null && !isSelected;
              return (
                <label
                  key={code}
                  className={`btn btn-outline-light rdb-choices${isSelected ? " active" : ""}${isDimmed ? " disabled" : ""}`}
                  style={{
                    opacity: isDimmed ? 0.35 : 1,
                    pointerEvents: tieBreakSelected !== null ? "none" : "auto",
                  }}
                  onClick={() => this.handleTieBreakPick(code)}
                >
                  <input
                    type="radio"
                    name="tiebreak-choices"
                    value={code}
                    checked={isSelected}
                    readOnly
                  />
                  <span style={{ fontSize: "1.2rem", lineHeight: "1.5" }}>
                    {TIE_BREAKER_ACTIVITY_KEYS[code] ? (
                      <Translate value={TIE_BREAKER_ACTIVITY_KEYS[code]} />
                    ) : (
                      code
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </OverlayCard>
      </OverlayBackdrop>
    );
  }

  render() {
    console.log("Assessment props in ActivityOptions:", this.props.assessment);
    const { selectedId, phase } = this.state;
    let letter = "";

    return (
      <div className="question">
        {this.props.assessment.has_security_features && (
          <Webcam
            style={{ position: "absolute", right: "100%", display: "block" }}
            screenshotFormat="image/jpeg"
            muted={false}
            ref={this.webcamRef}
            onUserMediaError={this.onUserMediaError}
            onUserMedia={this.onUserMedia}
          />
        )}

        <div>
          <QuestionNumber />
          <p className="quesion-sub"></p>
        </div>

        <h4>
          <span dangerouslySetInnerHTML={{ __html: this.props.questiontext }} />
        </h4>

        <div className="choices-btn" data-toggle="buttons">
          {!!this.props.choices &&
            this.props.choices.map((value) => {
              // if (letter === "") {
              //   letter = "A";
              // } else {
              //   letter = String.fromCharCode(letter.charCodeAt(0) + 1);
              // }

              const isSelected = selectedId === value.id;
              const isAnswered = selectedId !== null;

              return (
                <label
                  key={value.choice.id}
                  className={`btn btn-outline-light rdb-choices${isSelected ? " active" : ""}${isAnswered && !isSelected ? " disabled" : ""}`}
                  onClick={() => {
                    if (!isAnswered) this.handleSelect(value.id);
                  }}
                  style={{
                    pointerEvents: isAnswered && !isSelected ? "none" : "auto",
                  }}
                >
                  <input
                    type="radio"
                    name="choices"
                    id={"rdbChoice" + value.choice.id}
                    value={value.choice.id}
                    checked={isSelected}
                    readOnly
                  />
                  {/* <span className="letter-choice">{letter}</span> */}
                  <span
                    style={{ fontSize: "1.2rem", lineHeight: "1.5" }}
                    dangerouslySetInnerHTML={{ __html: value.choice.text }}
                  />
                </label>
              );
            })}
        </div>

        {phase === "tiebreak" && this.renderTieBreak()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = (dispatch) => ({
  nextQuestion: (question_id, choice_ids) =>
    dispatch(nextQuestion(question_id, choice_ids)),
  recordActivityCode: (code) => dispatch(recordActivityCode(code)),
  setActivityTopCodes: (codes) => dispatch(setActivityTopCodes(codes)),
  setActivityScores: (scores) => dispatch(setActivityScores(scores)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityOptions);
